import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { SEO } from "@/components/SEO";
import { Header } from "@/components/layout/Header";
import { Calendar, Clock, ArrowLeft, ChevronRight, ArrowUpRight, User } from "lucide-react";
import {
  getPostBySlug as getLocalPostBySlug,
  getSortedPosts as getLocalSortedPosts,
  type BlogPost,
} from "@/content/blog/posts";
import { getPostBySlug as getDbPostBySlug, listPosts as listDbPosts } from "@/lib/blog";
import { SITE_URL } from "@/config/site";
import { BlogCard } from "@/components/blog/BlogCard";


const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const BlogPost = () => {
  const { slug = "" } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(() => getLocalPostBySlug(slug) ?? null);
  const [allPosts, setAllPosts] = useState<BlogPost[]>(() => getLocalSortedPosts());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      try {
        const [dbPost, dbAll] = await Promise.all([
          getDbPostBySlug(slug, { publishedOnly: true }),
          listDbPosts({ publishedOnly: true }),
        ]);
        if (dbPost) setPost(dbPost);
        if (dbAll.length) setAllPosts(dbAll);
      } catch (e) {
        console.warn("BlogPost: falling back to local content", e);
      } finally {
        setLoaded(true);
      }
    })();
  }, [slug]);

  if (!post && loaded) return <Navigate to="/blog" replace />;
  if (!post) return null;

  const related = allPosts.filter((p) => p.slug !== post.slug).slice(0, 3);
  const coverUrl = post.coverImage.startsWith("http")
    ? post.coverImage
    : `${SITE_URL}${post.coverImage}`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.description,
      image: [coverUrl],
      datePublished: post.date,
      dateModified: post.date,
      articleSection: post.category,
      author: { "@type": "Organization", name: "BabbleGram" },
      publisher: {
        "@type": "Organization",
        name: "BabbleGram",
        logo: { "@type": "ImageObject", url: `${SITE_URL}/Babblegram-1.png` },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `${SITE_URL}/blog/${post.slug}`,
      },
      keywords: post.tags.join(", "),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "BabbleBlog", item: `${SITE_URL}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: `${SITE_URL}/blog/${post.slug}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#fbf9f4] text-slate-900 flex flex-col text-left">
      <SEO
        title={`${post.title} | BabbleBlog`}
        description={post.description}
        path={`/blog/${post.slug}`}
        image={post.coverImage}
        jsonLd={jsonLd}
      />
      <Header />

      <main className="flex-1 pt-20 pb-20">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="pt-6 text-xs text-slate-500">
            <ol className="flex items-center gap-1.5 flex-wrap">
              <li><Link to="/" className="hover:text-[#de0046] hover:underline underline-offset-2">Home</Link></li>
              <li aria-hidden="true"><ChevronRight size={12} /></li>
              <li><Link to="/blog" className="hover:text-[#de0046] hover:underline underline-offset-2">BabbleBlog</Link></li>
              <li aria-hidden="true"><ChevronRight size={12} /></li>
              <li aria-current="page" className="text-slate-700 font-medium line-clamp-1">{post.title}</li>
            </ol>
          </nav>

          <article>
            {/* Hero cover image — left aligned, capped width */}
            <figure className="mt-8 max-w-5xl">
              <div className="aspect-video overflow-hidden rounded-xl bg-slate-100 border border-slate-200">
                <img
                  src={post.coverImage}
                  alt={post.coverAlt}
                  width={1600}
                  height={896}
                  loading="eager"
                  fetchPriority="high"
                  className="h-full w-full object-cover"
                />
              </div>
            </figure>

            {/* Article header — single left column */}
            <header className="max-w-3xl mt-10">
              <p className="text-xs uppercase tracking-[0.22em] text-[#de0046] font-bold mb-4">
                {post.category}
              </p>
              <h1 className="font-serif text-3xl md:text-5xl leading-[1.1] tracking-tight text-slate-900">
                {post.title}
              </h1>
              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
                <span className="flex items-center gap-1.5">
                  <User size={14} aria-hidden="true" />
                  By <span className="text-slate-700 font-medium">BabbleGram team</span>
                </span>
                <span aria-hidden="true">·</span>
                <time dateTime={post.date} className="flex items-center gap-1.5">
                  <Calendar size={14} aria-hidden="true" />
                  {formatDate(post.date)}
                </time>
                <span aria-hidden="true">·</span>
                <span className="flex items-center gap-1.5">
                  <Clock size={14} aria-hidden="true" />
                  {post.readingMinutes} min read
                </span>
              </div>

              <p className="mt-6 pt-6 border-t border-slate-200 text-lg md:text-xl text-slate-600 leading-relaxed">
                {post.description}
              </p>
            </header>

            {/* Body — single left column */}
            <div
              className="
                prose prose-lg max-w-3xl mt-10
                font-serif
                prose-headings:font-serif prose-headings:text-slate-900 prose-headings:font-semibold prose-headings:tracking-tight
                prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:leading-snug
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-slate-800 prose-p:leading-[1.8] prose-p:text-[1.075rem]
                prose-li:text-slate-800 prose-li:leading-[1.8] prose-li:my-1
                prose-ul:my-5 prose-ol:my-5
                prose-strong:text-slate-900 prose-strong:font-semibold
                prose-em:text-slate-800
                prose-a:text-[#de0046] prose-a:no-underline hover:prose-a:underline prose-a:font-medium prose-a:underline-offset-2
                prose-blockquote:border-l-4 prose-blockquote:border-[#de0046] prose-blockquote:bg-white/60 prose-blockquote:py-2 prose-blockquote:px-5 prose-blockquote:text-slate-700 prose-blockquote:not-italic prose-blockquote:font-normal prose-blockquote:rounded-r
                prose-hr:border-slate-200 prose-hr:my-12
                prose-code:text-slate-900 prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:font-sans prose-code:text-sm prose-code:before:content-none prose-code:after:content-none
              "
            >
              {/^\s*</.test(post.body) ? (
                <div
                  className="quill-content w-full min-w-0 max-w-full"
                  dangerouslySetInnerHTML={{ __html: post.body }}
                />
              ) : (
                <ReactMarkdown>{post.body}</ReactMarkdown>
              )}
            </div>

            {/* Tags */}
            {post.tags.length > 0 && (
              <div className="max-w-3xl mt-12 pt-6 border-t border-slate-200">
                <h2 className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-3">
                  Tagged
                </h2>
                <ul className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <li
                      key={tag}
                      className="text-xs px-3 py-1.5 rounded-full bg-white text-slate-700 border border-slate-200"
                    >
                      #{tag}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Author bio */}
            <aside
              aria-label="About the author"
              className="max-w-3xl mt-10 p-5 md:p-6 rounded-xl border border-slate-200 bg-white flex items-start gap-4"
            >
              <div className="shrink-0 w-12 h-12 rounded-full bg-black flex items-center justify-center">
                <img
                  src="/lovable-uploads/f8f44bc9-ce01-42f1-98ff-b6aad7a934e9.png"
                  alt=""
                  className="w-9 h-9 object-contain"
                />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold">
                  Written by
                </p>
                <p className="font-serif text-lg text-slate-900 mt-0.5">The BabbleGram team</p>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                  We make a daily word chain puzzle and write occasionally about
                  the games, science, and history behind it.{" "}
                  <Link to="/blog" className="text-[#de0046] hover:underline underline-offset-2 font-medium">
                    More posts →
                  </Link>
                </p>
              </div>
            </aside>

            {/* Play CTA */}
            <aside
              aria-label="Play BabbleGram"
              className="max-w-3xl mt-6 p-6 md:p-8 rounded-xl bg-white border border-slate-200"
            >
              <p className="text-xs uppercase tracking-[0.22em] text-[#de0046] font-bold mb-2">
                Play today
              </p>
              <h2 className="font-serif text-2xl text-slate-900 mb-2">
                Try today's BabbleGram
              </h2>
              <p className="text-slate-700 mb-5 leading-relaxed">
                A daily word chain puzzle. Three minutes, one shared chain,
                streaks worth keeping.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#de0046] text-white font-semibold hover:bg-[#b8003a] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#de0046] focus-visible:ring-offset-2"
              >
                Play today's puzzle <ArrowUpRight size={16} />
              </Link>
            </aside>
          </article>

          {/* Related */}
          {related.length > 0 && (
            <section
              aria-labelledby="related-heading"
              className="mt-20 pt-12 border-t border-slate-200"
            >
              <div className="flex items-baseline justify-between mb-8">
                <h2
                  id="related-heading"
                  className="font-serif text-2xl md:text-3xl text-slate-900"
                >
                  Keep reading
                </h2>
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#de0046] hover:underline underline-offset-2"
                >
                  <ArrowLeft size={14} /> All articles
                </Link>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {related.map((p) => (
                  <li key={p.slug}>
                    <BlogCard post={p} />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </main>
    </div>
  );
};

export default BlogPost;
